import React from 'react';
import { Box, IconButton, useMediaQuery, useTheme, SxProps, Theme } from '@mui/material';
import Iconify from './iconify';

export interface HorizontalPagerProps<T> {
    items: T[];
    getItemKey: (item: T, index: number) => string;
    renderItem: (item: T, index: number) => React.ReactNode;
    activeIndex?: number | null;
    className?: string;
    sx?: SxProps<Theme>;
	spacing?: any
}

/**
 * @template T
 * @param {object} props
 * @param {T[]} props.items Items to render
 * @param {(item: T, index: number) => string} props.getItemKey Stable key getter
 * @param {(item: T, index: number) => React.ReactNode} props.renderItem Item renderer
 * @param {number|null} [props.activeIndex] Index to bring into view (optional)
 */
export default function HorizontalPager<T>({
    items,
    getItemKey,
    renderItem,
    activeIndex = null,
    className,
    sx,
	spacing
}: HorizontalPagerProps<T>) {
	const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'));
    const isMobile = !smUp;
    const gap = spacing ? spacing : 8;

	const scrollRef = React.useRef<HTMLDivElement | null>(null);
	const measureRef = React.useRef<HTMLDivElement | null>(null);
	const itemRefs = React.useRef<Map<string, HTMLElement | null>>(new Map());

	const [pages, setPages] = React.useState<number[][]>([]);
	const [currentPage, setCurrentPage] = React.useState(0);
	const [containerWidth, setContainerWidth] = React.useState(0);
	const lastRequestedPageRef = React.useRef(0);
	const isAnimatingRef = React.useRef(false);
	const animFrameRef = React.useRef<number | null>(null);
	const animStartTimeRef = React.useRef(0);
	const animStartLeftRef = React.useRef(0);
	const animTargetLeftRef = React.useRef(0);
    const animDurationRef = React.useRef(300);
    const blurMaxRef = React.useRef(0.7);

    // keep blurMax constant; no external control

	const totalPages = Math.max(1, pages.length);

    const ease = React.useMemo(() => (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2), []);

	const scrollToPage = React.useCallback((index: number) => {
		const el = scrollRef.current;
		if (!el) return;
		const clamped = Math.max(0, Math.min(index, totalPages - 1));
		lastRequestedPageRef.current = clamped;
		const viewport = el.clientWidth || el.getBoundingClientRect().width || 0;
		if (viewport < 2) {
			// Container not laid out yet; retry next frame
			requestAnimationFrame(() => scrollToPage(index));
			return;
		}
		const startLeft = el.scrollLeft;
		const targetLeft = clamped * viewport;
		const deltaPages = Math.abs(targetLeft - startLeft) / viewport;
		const duration = Math.min(380, Math.max(220, Math.round(220 + 80 * deltaPages)));

		if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
		isAnimatingRef.current = true;
		animStartTimeRef.current = 0;
		animStartLeftRef.current = startLeft;
		animTargetLeftRef.current = targetLeft;
		animDurationRef.current = duration;

		// Temporarily disable scroll-snap to avoid snapping fighting the animation
		const prevSnap = (el as any).style.scrollSnapType;
		try { (el as any).style.scrollSnapType = 'none'; } catch {}
		try { (el as any).style.scrollBehavior = 'auto'; } catch {}

		const step = (ts: number) => {
			if (!animStartTimeRef.current) animStartTimeRef.current = ts;
			const elapsed = ts - animStartTimeRef.current;
			const t = Math.min(1, elapsed / animDurationRef.current);
			const eased = ease(t);
			const nextLeft = animStartLeftRef.current + (animTargetLeftRef.current - animStartLeftRef.current) * eased;
			el.scrollLeft = nextLeft;
			const blurIntensity = (1 - Math.abs(0.5 - t) * 2) * blurMaxRef.current;
			(el as any).style.filter = `blur(${blurIntensity}px)`;
			if (t < 1) {
				animFrameRef.current = requestAnimationFrame(step);
			} else {
				el.scrollLeft = animTargetLeftRef.current;
				(el as any).style.filter = 'none';
				isAnimatingRef.current = false;
				setCurrentPage(clamped);
				animFrameRef.current = null;

				// Restore scroll-snap next frame to let it reapply for subsequent interactions
				requestAnimationFrame(() => {
					try { (el as any).style.scrollSnapType = prevSnap || 'x mandatory'; } catch {}
				});
			}
		};
		animFrameRef.current = requestAnimationFrame(step);
    }, [totalPages, ease]);

	const handlePrev = React.useCallback(() => scrollToPage(currentPage - 1), [currentPage, scrollToPage]);
	const handleNext = React.useCallback(() => scrollToPage(currentPage + 1), [currentPage, scrollToPage]);

	const handleScroll = React.useCallback((e: React.UIEvent<HTMLDivElement>) => {
		const el = e.currentTarget;
		if (!el.clientWidth) return;
        if (isAnimatingRef.current) return;
		const idx = Math.round(el.scrollLeft / el.clientWidth);
		if (idx !== currentPage) {
			lastRequestedPageRef.current = idx;
			setCurrentPage(idx);
            
		}
    }, [currentPage]);

	// Resize Observer + reset filter after animation (safety)
	React.useEffect(() => {
		const el = scrollRef.current;
		if (!el) return;
		const ro = new (window as any).ResizeObserver((entries: any) => {
			for (const entry of entries) {
				const w = entry.contentRect?.width ?? el.clientWidth;
                if (w && w !== containerWidth) setContainerWidth(w);
			}
		});
		ro.observe(el);
		setContainerWidth(el.clientWidth);
		return () => {
			try { ro.disconnect(); } catch {}
            try { (el as any).style.filter = 'none'; } catch {}
		};
	}, []);

	// Compute pages based on measured widths
	React.useEffect(() => {
		if (!containerWidth) return;
		if (!items.length) { setPages([]); setCurrentPage(0); return; }

		const widths: number[] = [];
		let ready = true;
    items.forEach((item, index) => {
			const key = getItemKey(item, index);
			const el = itemRefs.current.get(key);
			const w = el?.offsetWidth ?? 0;
			if (!w) ready = false;
			widths.push(w);
		});
		if (!ready) {
			const id = requestAnimationFrame(() => setContainerWidth((w) => w));
			return () => cancelAnimationFrame(id);
		}

		const newPages: number[][] = [];
		let current: number[] = [];
		let lineWidth = 0;
		items.forEach((_, idx) => {
			const w = widths[idx];
			const extraGap = current.length > 0 ? gap : 0;
			if (lineWidth + extraGap + w <= containerWidth || current.length === 0) {
				lineWidth += extraGap + w;
				current.push(idx);
			} else {
				if (current.length) newPages.push(current);
				current = [idx];
				lineWidth = w;
			}
		});
		if (current.length) newPages.push(current);

		setPages(newPages);
        const desired = activeIndex != null
			? Math.max(0, newPages.findIndex((page) => page.includes(activeIndex)))
			: Math.max(0, Math.min(lastRequestedPageRef.current, newPages.length - 1));
		requestAnimationFrame(() => scrollToPage(desired));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerWidth, items, gap, activeIndex]);

	return (
        <Box className={className} sx={{ position: 'relative', width: '100%', ...sx }}>
			<Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0 }}>
				{/* Left arrow column always reserved on desktop to keep viewport width stable */}
				<Box sx={{ width: !isMobile ? `40px` : 0, display: 'flex', justifyContent: 'center' }}>
					{!isMobile && (
						<IconButton color="inherit" size="small" onClick={handlePrev} disabled={currentPage <= 0}>
							<Iconify icon="mdi:chevron-left" />
						</IconButton>
					)}
				</Box>

                <Box
					ref={scrollRef}
					onScroll={handleScroll}
					sx={{
						display: 'flex',
						overflowX: 'auto',
						scrollSnapType: 'x mandatory',
						gap: 0,
						width: '100%',
						flex: 1,
						'::-webkit-scrollbar': { display: 'none' },
						scrollbarWidth: 'none',
                        willChange: 'scroll-position, filter',
						transform: 'translateZ(0)',
						contain: 'paint',
					}}
				>
					{pages.map((page, pageIndex) => (
						<Box
							key={`page-${pageIndex}`}
							sx={{
								flex: '0 0 100%',
								scrollSnapAlign: 'start',
								display: 'flex',
								gap: `${gap}px`,
								px: 0,
								justifyContent: 'flex-start',
							}}
						>
                            {page.map((itemIndex) => (
								<Box key={getItemKey(items[itemIndex], itemIndex)} sx={{ flex: '0 0 auto' }}>
									{renderItem(items[itemIndex], itemIndex)}
								</Box>
							))}
						</Box>
					))}
				</Box>


				{/* Right arrow column always reserved on desktop */}
				<Box sx={{ width: !isMobile ? `40px` : 0, display: 'flex', justifyContent: 'center' }}>
					{!isMobile && (
						<IconButton color="inherit" size="small" onClick={handleNext} disabled={currentPage >= totalPages - 1}>
							<Iconify icon="mdi:chevron-right" />
						</IconButton>
					)}
				</Box>
			</Box>

            {isMobile && pages.length > 1 && (
				<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 0.5 }}>
					{Array.from({ length: totalPages }).map((_, idx) => (
						<Box
							key={`dot-${idx}`}
							onClick={() => scrollToPage(idx)}
							sx={{
								width: 6,
								height: 6,
								borderRadius: '50%',
								cursor: 'pointer',
								backgroundColor: idx === currentPage ? '#fff' : 'rgba(255,255,255,0.4)',
								transition: 'background-color 0.2s ease',
							}}
						/>
					))}
				</Box>
			)}

			{/* Hidden measurement container */}
            <Box ref={measureRef} aria-hidden sx={{ position: 'absolute', visibility: 'hidden', pointerEvents: 'none', height: 0, overflow: 'hidden' }}>
				<Box sx={{ display: 'inline-flex', gap: `${gap}px` }}>
					{items.map((item, index) => {
						const key = getItemKey(item, index);
						return (
							<Box
								key={`measure-${key}`}
								ref={(el: HTMLDivElement | null) => itemRefs.current.set(key, el)}
							>
                                {renderItem(item, index)}
							</Box>
						);
					})}
				</Box>
			</Box>
		</Box>
	);
}


